// src/components/avatar/seasons/seasonData.js
//
// The 12 seasons (holidays) of the shop and avatar maker. Anyone can open a
// season and try its things on all year; buying is only open between
// `from` and `until` ("MM-DD", every year; a window may wrap the new year).
// Holidays that move (Easter, Lunar New Year, Diwali, Hanukkah) get a wide
// window that covers their dates for years to come.
//
// The server reads the same windows (functions keep a copy of the ids and
// dates — keep them in step). An admin can move a season's dates, open it
// early or close it in Layout Builder ▸ 🗓 Season dates; both sides read
// those changes from rewardSettings/seasons.

export const SEASONS = [
  { id: "newyear", name: "New Year's", emoji: "🎆", from: "12-15", until: "01-10", palette: { main: "#1f2a5c", second: "#f2c230", accent: "#ff5fa2", light: "#fff3c4" } },
  { id: "lunarnewyear", name: "Lunar New Year", emoji: "🏮", from: "01-10", until: "02-28", palette: { main: "#d62828", second: "#f6c343", accent: "#ff8c42", light: "#ffe7c2" } },
  { id: "valentines", name: "Valentine's Day", emoji: "💝", from: "01-25", until: "02-16", palette: { main: "#e83f6f", second: "#ffb3c8", accent: "#c81d4e", light: "#ffe4ec" } },
  { id: "stpatricks", name: "St. Patrick's Day", emoji: "☘️", from: "02-24", until: "03-20", palette: { main: "#1f9e4a", second: "#f2c230", accent: "#7fd36b", light: "#e3f8e6" } },
  { id: "easter", name: "Easter", emoji: "🐣", from: "03-01", until: "04-30", palette: { main: "#b69cf2", second: "#ffe27a", accent: "#7fd8be", light: "#fdf3ff" } },
  { id: "july4", name: "4th of July", emoji: "🎇", from: "06-15", until: "07-10", palette: { main: "#1f3f8f", second: "#e23b4a", accent: "#ffffff", light: "#e6eeff" } },
  { id: "halloween", name: "Halloween", emoji: "🎃", from: "10-01", until: "11-02", palette: { main: "#ff8a1f", second: "#6b3fa0", accent: "#7ed957", light: "#ffeedd" } },
  { id: "diwali", name: "Diwali", emoji: "🪔", from: "10-05", until: "11-20", palette: { main: "#ff9f1c", second: "#d6246e", accent: "#f6c343", light: "#fff1d6" } },
  { id: "thanksgiving", name: "Thanksgiving", emoji: "🦃", from: "11-01", until: "11-30", palette: { main: "#c8641e", second: "#8a4b22", accent: "#f2b134", light: "#fbead2" } },
  { id: "hanukkah", name: "Hanukkah", emoji: "🕎", from: "11-20", until: "01-03", palette: { main: "#2a5bd7", second: "#c9d3e6", accent: "#f2c230", light: "#e6efff" } },
  { id: "christmas", name: "Christmas", emoji: "🎄", from: "11-25", until: "01-06", palette: { main: "#d8343b", second: "#2fae5b", accent: "#f2c230", light: "#fff2f0" } },
  { id: "kwanzaa", name: "Kwanzaa", emoji: "🕯️", from: "12-10", until: "01-03", palette: { main: "#c62f2f", second: "#1f8a3e", accent: "#2a2420", light: "#fff4dc" } },
];

export const SEASON_IDS = SEASONS.map((s) => s.id);
const BY_ID = Object.fromEntries(SEASONS.map((s) => [s.id, s]));

export const seasonOf = (id) => BY_ID[id] || null;

const pad = (n) => String(n).padStart(2, "0");
const nyDate = (date) => new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));

// ---------------------------------------------------------------------------
// ADMIN CHANGES (Layout Builder ▸ 🗓 Season dates, saved to
// rewardSettings/seasons). Per season: { mode: "dates" | "open" | "closed",
// from, until }. "open" = on sale now whatever the date (a class party),
// "closed" = not on sale this year. seasonLive.js keeps this up to date.
// ---------------------------------------------------------------------------
let OVERRIDES = {};
export const setSeasonOverrides = (o) => {
  OVERRIDES = o && typeof o === "object" ? o : {};
};
const MD = /^\d{2}-\d{2}$/;

/** The season's buy window right now: { from, until, mode } */
export const seasonWindow = (seasonId) => {
  const s = BY_ID[seasonId];
  if (!s) return null;
  const o = OVERRIDES[seasonId] || {};
  return {
    mode: ["open", "closed"].includes(o.mode) ? o.mode : "dates",
    from: MD.test(o.from || "") ? o.from : s.from,
    until: MD.test(o.until || "") ? o.until : s.until,
    changed: Boolean(OVERRIDES[seasonId]),
  };
};

/** Is buying open for this season on `date` (New York time)? */
export const seasonOpen = (seasonId, date = new Date()) => {
  const w = seasonWindow(seasonId);
  if (!w) return false;
  if (w.mode === "open") return true;
  if (w.mode === "closed") return false;
  const ny = nyDate(date);
  const md = `${pad(ny.getMonth() + 1)}-${pad(ny.getDate())}`;
  const f = w.from || "01-01";
  const u = w.until || "12-31";
  return f <= u ? md >= f && md <= u : md >= f || md <= u;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const niceDay = (md) => {
  const [m, d] = String(md).split("-").map(Number);
  return `${MONTHS[(m || 1) - 1]} ${d || 1}`;
};

/** "On sale from Dec 1" (closed) or "On sale until Jan 6" (open). */
export const nextOpenLabel = (seasonId, date = new Date()) => {
  const w = seasonWindow(seasonId);
  if (!w) return "";
  if (w.mode === "open") return "On sale now";
  if (w.mode === "closed") return "Not on sale this year";
  return seasonOpen(seasonId, date) ? `On sale until ${niceDay(w.until)}` : `On sale from ${niceDay(w.from)}`;
};

/** Seasons open right now, in calendar order. */
export const openSeasons = (date = new Date()) => SEASONS.filter((s) => seasonOpen(s.id, date));
